import { useSyncExternalStore } from "react";

/*
 * Theme preference (Kuest ThemeSelector: light / system / dark). The portal's dark mode
 * is still the `dark` class on <body>; this module decides when it is on and remembers
 * the choice. Light stays the default, as before.
 */
const STORAGE_KEY = "wasaa_theme";
const PREFS = ["light", "system", "dark"];
const listeners = new Set();
let pref = readStored();
let media = null;

function readStored() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return PREFS.includes(value) ? value : "light";
  } catch {
    return "light";
  }
}

function systemDark() {
  return typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)").matches : false;
}

export function resolvedTheme(p = pref) {
  return p === "system" ? (systemDark() ? "dark" : "light") : p;
}

function apply() {
  if (typeof document === "undefined" || !document.body) return;
  document.body.classList.toggle("dark", resolvedTheme() === "dark");
}

function emit() {
  listeners.forEach((listener) => listener());
}

function onSystemChange() {
  if (pref === "system") {
    apply();
    emit();
  }
}

/** Apply the stored preference once at start-up and follow the OS while on "system". */
export function initTheme() {
  apply();
  if (!media && typeof window !== "undefined" && window.matchMedia) {
    media = window.matchMedia("(prefers-color-scheme: dark)");
    if (media.addEventListener) media.addEventListener("change", onSystemChange);
    else if (media.addListener) media.addListener(onSystemChange);
  }
}

export function getThemePreference() {
  return pref;
}

export function setThemePreference(next) {
  if (!PREFS.includes(next)) return;
  pref = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // storage unavailable: the choice still applies for this page load
  }
  apply();
  emit();
}

/** Flip between light and dark (the header's quick toggle). */
export function toggleTheme() {
  setThemePreference(resolvedTheme() === "dark" ? "light" : "dark");
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot() {
  return `${pref}:${resolvedTheme()}`;
}

/** { preference: "light" | "system" | "dark", theme: "light" | "dark" } */
export function useTheme() {
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);
  const [preference, theme] = value.split(":");
  return { preference, theme };
}
